import { Module, ActionContext } from 'vuex';
import { RootState } from '@/types/store';
import {
  ChatState,
  Conversation,
  Message,
  User,
  Product,
} from '@/types/chat-types';
import chatService from '@/services/chatService';

// Ažuriran interface za ChatState
interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ExtendedChatState extends ChatState {
  conversationsMeta: PaginationMeta | null;
}

// Inicijalno stanje
const state: ExtendedChatState = {
  conversations: [],
  conversationsMeta: null,
  currentConversation: null,
  isLoading: false,
  error: null,
};

// Getter-i
const getters = {
  allConversations: (state: ExtendedChatState): Conversation[] =>
    state.conversations,
  conversationsMeta: (state: ExtendedChatState): PaginationMeta | null =>
    state.conversationsMeta,
  currentConversation: (state: ExtendedChatState) => state.currentConversation,
  currentMessages: (state: ExtendedChatState): Message[] =>
    state.currentConversation ? state.currentConversation.messages : [],
  isLoading: (state: ExtendedChatState): boolean => state.isLoading,
  chatError: (state: ExtendedChatState): string | null => state.error,
  unreadMessageCount: (state: ExtendedChatState): number => {
    return state.conversations.reduce(
      (total, conv) => total + conv.unreadCount,
      0
    );
  },
};

// Akcije
const actions = {
  // Ažurirano učitavanje konverzacija sa paginacijom
  async fetchConversations(
    { commit }: ActionContext<ExtendedChatState, RootState>,
    { page = 1, limit = 6 }: { page?: number; limit?: number } = {}
  ) {
    try {
      commit('SET_LOADING', true);

      const response = await chatService.getConversations(page, limit);

      commit('SET_CONVERSATIONS', {
        conversations: response.data,
        meta: response.meta,
      });

      commit('SET_LOADING', false);
    } catch (error) {
      commit('SET_LOADING', false);
      commit(
        'SET_ERROR',
        error instanceof Error ? error.message : 'Failed to load conversations'
      );
      throw error;
    }
  },

  // Učitavanje poruka za određenu konverzaciju
  async fetchConversationMessages(
    { commit }: ActionContext<ExtendedChatState, RootState>,
    { otherUserId, productId }: { otherUserId: string; productId: string }
  ) {
    try {
      commit('SET_LOADING', true);

      const messages = await chatService.getConversationMessages(
        otherUserId,
        productId
      );

      commit('SET_CURRENT_CONVERSATION', {
        otherUserId,
        productId,
        messages,
      });

      commit('RESET_UNREAD_COUNT', { otherUserId, productId });
      commit('SET_LOADING', false);

      return messages;
    } catch (error) {
      commit('SET_LOADING', false);
      commit(
        'SET_ERROR',
        error instanceof Error ? error.message : 'Failed to load messages'
      );
      throw error;
    }
  },

  // Slanje nove poruke
  async sendMessage(
    { commit, state, dispatch }: ActionContext<ExtendedChatState, RootState>,
    {
      receiverId,
      productId,
      content,
    }: { receiverId: string; productId: string; content: string }
  ) {
    try {
      if (!content.trim()) {
        return;
      }

      const message = await chatService.sendMessage(
        receiverId,
        productId,
        content
      );

      // Dodajemo poruku u trenutnu konverzaciju
      if (
        state.currentConversation &&
        state.currentConversation.otherUserId === receiverId &&
        state.currentConversation.productId === productId
      ) {
        commit('ADD_MESSAGE', message);
      }

      // Ažuriramo lastMessage u listi konverzacija
      commit('UPDATE_CONVERSATION_LAST_MESSAGE', {
        otherUserId: receiverId,
        productId,
        message: {
          id: message.id,
          content: message.content,
          createdAt: message.createdAt,
          isRead: message.isRead,
        },
      });

      return message;
    } catch (error) {
      commit(
        'SET_ERROR',
        error instanceof Error ? error.message : 'Failed to send message'
      );
      throw error;
    }
  },

  // Nova action za force refresh liste konverzacija
  async refreshCurrentPage({
    commit,
    state,
  }: ActionContext<ExtendedChatState, RootState>) {
    if (!state.conversationsMeta) {
      return;
    }

    try {
      const response = await chatService.getConversations(
        state.conversationsMeta.page,
        state.conversationsMeta.limit
      );

      commit('SET_CONVERSATIONS', {
        conversations: response.data,
        meta: response.meta,
      });
    } catch (error) {
      throw error;
    }
  },

  // Brisanje konverzacije sa refresh trenutne stranice
  async deleteConversation(
    { commit, state, dispatch }: ActionContext<ExtendedChatState, RootState>,
    { otherUserId, productId }: { otherUserId: string; productId: string }
  ) {
    try {
      await chatService.deleteConversation(otherUserId, productId);

      commit('REMOVE_CONVERSATION', { otherUserId, productId });
      commit('RESET_CURRENT_CONVERSATION_IF_MATCH', { otherUserId, productId });

      return true;
    } catch (error) {
      commit(
        'SET_ERROR',
        error instanceof Error ? error.message : 'Failed to delete conversation'
      );
      throw error;
    }
  },

  // Dodavanje nove konverzacije (kada korisnik pošalje prvu poruku)
  addNewConversation(
    { commit }: ActionContext<ExtendedChatState, RootState>,
    conversation: Conversation
  ) {
    commit('ADD_CONVERSATION', conversation);
  },

  // Čišćenje trenutne konverzacije
  clearCurrentConversation({
    commit,
  }: ActionContext<ExtendedChatState, RootState>) {
    commit('SET_CURRENT_CONVERSATION', null);
  },

  // Označavanje konverzacije kao pročitane
  markConversationAsRead(
    { commit }: ActionContext<ExtendedChatState, RootState>,
    { otherUserId, productId }: { otherUserId: string; productId: string }
  ) {
    commit('RESET_UNREAD_COUNT', { otherUserId, productId });
  },
};

// Mutacije
const mutations = {
  // Ažurirano postavljanje konverzacija sa meta podacima
  SET_CONVERSATIONS(
    state: ExtendedChatState,
    {
      conversations,
      meta,
    }: { conversations: Conversation[]; meta: PaginationMeta }
  ) {
    state.conversations = conversations;
    state.conversationsMeta = meta;
  },

  // Dodavanje nove konverzacije na vrh liste
  ADD_CONVERSATION(state: ExtendedChatState, conversation: Conversation) {
    // Proveravamo da li konverzacija već postoji
    const existingIndex = state.conversations.findIndex(
      (conv) =>
        conv.otherUser.id === conversation.otherUser.id &&
        conv.product.id === conversation.product.id
    );

    if (existingIndex === -1) {
      // Dodajemo novu konverzaciju na vrh
      state.conversations.unshift(conversation);

      // Ažuriramo meta podatke ako postoje
      if (state.conversationsMeta) {
        state.conversationsMeta.total += 1;
        state.conversationsMeta.totalPages = Math.ceil(
          state.conversationsMeta.total / state.conversationsMeta.limit
        );
      }
    } else {
      // Ažuriramo postojeću konverzaciju (prebacujemo na vrh)
      const existingConversation = state.conversations[existingIndex];
      state.conversations.splice(existingIndex, 1);
      state.conversations.unshift({ ...existingConversation, ...conversation });
    }
  },

  // Uklanjanje konverzacije iz liste
  REMOVE_CONVERSATION(
    state: ExtendedChatState,
    { otherUserId, productId }: { otherUserId: string; productId: string }
  ) {
    const initialLength = state.conversations.length;
    state.conversations = state.conversations.filter(
      (c) => !(c.otherUser.id === otherUserId && c.product.id === productId)
    );

    // Ažuriramo meta podatke ako je konverzacija stvarno uklonjena
    if (state.conversations.length < initialLength && state.conversationsMeta) {
      state.conversationsMeta.total -= 1;
      state.conversationsMeta.totalPages = Math.ceil(
        state.conversationsMeta.total / state.conversationsMeta.limit
      );
    }
  },

  SET_CURRENT_CONVERSATION(
    state: ExtendedChatState,
    conversation: {
      otherUserId: string;
      productId: string;
      messages: Message[];
      otherUser?: any;
      product?: any;
    } | null
  ) {
    state.currentConversation = conversation;
  },

  ADD_MESSAGE(state: ExtendedChatState, message: Message) {
    if (state.currentConversation) {
      state.currentConversation.messages = [
        ...state.currentConversation.messages,
        message,
      ];
    }
  },

  UPDATE_CONVERSATION_LAST_MESSAGE(
    state: ExtendedChatState,
    {
      otherUserId,
      productId,
      message,
    }: {
      otherUserId: string;
      productId: string;
      message: {
        id: string;
        content: string;
        createdAt: string;
        isRead: boolean;
      };
    }
  ) {
    // Prvo pronađemo konverzaciju koju treba ažurirati
    const conversationIndex = state.conversations.findIndex(
      (c) => c.otherUser.id === otherUserId && c.product.id === productId
    );

    if (conversationIndex !== -1) {
      // Kreiraemo POTPUNO NOVU kopiju konverzacije zbog Vue reaktivnosti
      const oldConversation = state.conversations[conversationIndex];
      const updatedConversation = {
        ...oldConversation,
        lastMessage: { ...message },
      };

      // Uklanjamo staru konverzaciju
      state.conversations.splice(conversationIndex, 1);

      // Dodajemo ažuriranu konverzaciju na vrh
      state.conversations.unshift(updatedConversation);
    } else {
      // Kreiraj novu konverzaciju i dodaj je na vrh
      if (state.currentConversation) {
        const otherUserObj: User = state.currentConversation.otherUser || {
          id: otherUserId,
          username: 'Korisnik',
        };

        const productObj: Product = state.currentConversation.product || {
          id: productId,
          name: 'Proizvod',
          slug: '',
        };

        const newConversation: Conversation = {
          otherUser: otherUserObj,
          product: productObj,
          lastMessage: { ...message },
          unreadCount: 0,
        };

        // Dodaj novu konverzaciju na vrh liste
        state.conversations.unshift(newConversation);

        // Ažuriraj meta podatke
        if (state.conversationsMeta) {
          state.conversationsMeta.total += 1;
          state.conversationsMeta.totalPages = Math.ceil(
            state.conversationsMeta.total / state.conversationsMeta.limit
          );
        }
      }
    }
  },

  RESET_UNREAD_COUNT(
    state: ExtendedChatState,
    { otherUserId, productId }: { otherUserId: string; productId: string }
  ) {
    const conversation = state.conversations.find(
      (c) => c.otherUser.id === otherUserId && c.product.id === productId
    );

    if (conversation) {
      conversation.unreadCount = 0;
    }
  },

  UPDATE_UNREAD_COUNT(
    state: ExtendedChatState,
    {
      otherUserId,
      productId,
      count,
    }: {
      otherUserId: string;
      productId: string;
      count: number;
    }
  ) {
    const conversation = state.conversations.find(
      (c) => c.otherUser.id === otherUserId && c.product.id === productId
    );

    if (conversation) {
      conversation.unreadCount = count;
    }
  },

  RESET_CURRENT_CONVERSATION_IF_MATCH(
    state: ExtendedChatState,
    { otherUserId, productId }: { otherUserId: string; productId: string }
  ) {
    if (
      state.currentConversation &&
      state.currentConversation.otherUserId === otherUserId &&
      state.currentConversation.productId === productId
    ) {
      state.currentConversation = null;
    }
  },

  SET_LOADING(state: ExtendedChatState, isLoading: boolean) {
    state.isLoading = isLoading;
  },

  SET_ERROR(state: ExtendedChatState, error: string | null) {
    state.error = error;
  },
};

// Eksportujemo Vuex modul
const chatModule: Module<ExtendedChatState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default chatModule;
